import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, DatePicker, Descriptions, Drawer, Form, Input, Pagination, Select, Space, Table, Tag, Typography, message } from 'antd';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { listReceivedDebtIntents } from '../../services/debt';
import { labelOfDebtStatus, labelOfDebtType } from '../../constants/debtEnums';
import { formatAmountText } from '../../utils/numberFormat';
import { PAGINATION_DEFAULT_PAGE_SIZE, PAGINATION_ENABLE_SIZE_CHANGER, PAGINATION_PAGE_SIZE_OPTIONS } from '../../config/theme';
import { useBreakpoint } from '../../hooks/useBreakpoint';

const DEFAULT_PAGINATION = { current: 1, pageSize: PAGINATION_DEFAULT_PAGE_SIZE, total: 0 };
const DEFAULT_SORTER = { field: 'create_time', order: 'desc' };
const normalizePageSize = (size) => (PAGINATION_ENABLE_SIZE_CHANGER ? Number(size || PAGINATION_DEFAULT_PAGE_SIZE) : PAGINATION_DEFAULT_PAGE_SIZE);

const intentTypeLabel = (v) => {
  if (v === 'INVEST') return '投资';
  if (v === 'AGENCY_COLLECTION') return '代理催收';
  return v || '-';
};

const MyDebtIntentsPage = () => {
  const navigate = useNavigate();
  const { isMobile } = useBreakpoint();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [sorter, setSorter] = useState(DEFAULT_SORTER);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);

  const disabledSubmitDate = useCallback((current) => {
    if (!current) return false;
    const max = dayjs().endOf('day');
    const min = dayjs().subtract(1, 'month').startOf('day');
    return current.isAfter(max, 'day') || current.isBefore(min, 'day');
  }, []);

  const fetchList = useCallback(async (pageCfg, values) => {
    const pageSize = normalizePageSize(pageCfg?.pageSize);
    const current = Number(pageCfg?.current || 1);
    setLoading(true);
    try {
      const v = values || form.getFieldsValue();
      const params = {
        page: current,
        size: pageSize,
        sort_field: sorter?.field || DEFAULT_SORTER.field,
        sort_order: sorter?.order || DEFAULT_SORTER.order,
        need_total: true,
      };

      const keyword = (v?.keyword || '').trim();
      if (keyword) params.keyword = keyword;

      const intentType = (v?.intent_type || '').trim();
      if (intentType) params.intent_type = intentType;

      const submitDate = v?.submit_date;
      if (submitDate) {
        const d = dayjs(submitDate).format('YYYY-MM-DD');
        params.start_date = d;
        params.end_date = d;
      }

      const { data } = await listReceivedDebtIntents(params);
      const list = Array.isArray(data?.records) ? data.records : [];
      setRecords(list);
      setPagination({
        current: Number(data?.current || current),
        pageSize: normalizePageSize(data?.size || pageSize),
        total: Number(data?.total ?? 0),
      });
    } catch (e) {
      message.error(e?.message || '加载投资代理意向失败');
    } finally {
      setLoading(false);
    }
  }, [form, sorter?.field, sorter?.order]);

  useEffect(() => {
    fetchList({ current: 1, pageSize: pagination.pageSize });
  }, [fetchList, pagination.pageSize]);

  const handleSearch = (values) => {
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchList({ current: 1, pageSize: pagination.pageSize }, values);
  };

  const handleReset = () => {
    form.resetFields();
    setSorter(DEFAULT_SORTER);
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchList({ current: 1, pageSize: pagination.pageSize }, {});
  };

  const handleTableChange = (_, __, s) => {
    if (!s || !s.field) return;
    const field = s.field;
    const order = s.order === 'ascend' ? 'asc' : s.order === 'descend' ? 'desc' : DEFAULT_SORTER.order;
    const sortField = field === 'createTime' ? 'create_time'
      : field === 'debtNo' ? 'debt_no'
      : field === 'totalAmount' ? 'total_amount'
      : DEFAULT_SORTER.field;
    setSorter({ field: sortField, order });
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchList({ current: 1, pageSize: pagination.pageSize });
  };

  const handlePaginationChange = (current, pageSize) => {
    const normalized = normalizePageSize(pageSize);
    setPagination(prev => ({ ...prev, current, pageSize: normalized }));
    fetchList({ current, pageSize: normalized });
  };

  const openDetail = (row) => {
    setDetail(row || null);
    setDetailOpen(true);
  };

  const openDebtDetail = (row) => {
    if (!row?.debtId) return;
    navigate(`/debt-management/detail/${row.debtId}`);
  };

  const intentTypeOptions = useMemo(() => ([
    { label: '全部', value: '' },
    { label: '投资', value: 'INVEST' },
    { label: '代理催收', value: 'AGENCY_COLLECTION' },
  ]), []);

  const columns = [
    {
      title: '提交时间',
      dataIndex: 'createTime',
      key: 'createTime',
      width: 170,
      sorter: true,
      render: (v) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm') : ''),
    },
    {
      title: '类型',
      dataIndex: 'intentType',
      key: 'intentType',
      width: 110,
      render: (v) => {
        const color = v === 'INVEST' ? 'blue' : v === 'AGENCY_COLLECTION' ? 'gold' : 'default';
        return <Tag color={color}>{intentTypeLabel(v)}</Tag>;
      },
    },
    {
      title: '债权编号',
      dataIndex: 'debtNo',
      key: 'debtNo',
      width: 150,
      sorter: true,
      render: (v) => v || '-',
    },
    {
      title: '债权名称',
      dataIndex: 'debtTitle',
      key: 'debtTitle',
      width: 240,
      ellipsis: true,
      render: (v, record) => {
        if (!v) return '-';
        if (!record?.debtId) return v;
        return (
          <Typography.Link
            className="u-ellipsis-inline"
            onClick={(e) => {
              e.stopPropagation();
              openDebtDetail(record);
            }}
          >
            {v}
          </Typography.Link>
        );
      },
    },
    {
      title: '债权类型',
      dataIndex: 'debtType',
      key: 'debtType',
      width: 120,
      render: (v) => labelOfDebtType(v),
    },
    {
      title: '合计金额',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: 120,
      sorter: true,
      align: 'right',
      className: 'u-tabular-nums',
      render: (v) => formatAmountText(v),
    },
    {
      title: '债权状态',
      dataIndex: 'debtStatus',
      key: 'debtStatus',
      width: 120,
      render: (v) => {
        const color = v === 'PUBLISHED' ? 'green' : v === 'PENDING' ? 'orange' : 'default';
        return <Tag color={color}>{labelOfDebtStatus(v)}</Tag>;
      },
    },
    {
      title: '申请人',
      dataIndex: 'applicantName',
      key: 'applicantName',
      width: 120,
      render: (v) => v || '-',
    },
    {
      title: '联系电话',
      dataIndex: 'contactPhone',
      key: 'contactPhone',
      width: 140,
      render: (v) => v || '-',
    },
    {
      title: '备注',
      dataIndex: 'message',
      key: 'message',
      ellipsis: true,
      render: (v) => v || '-',
    },
  ];

  return (
    <div className="page-container p-debt-management-my-intents">
      <div className="page-content page-content-wide space-y-md">
        <div className="page-title">投资代理</div>

        <Card className="card-compact">
          <Form layout={isMobile ? 'vertical' : 'inline'} form={form} onFinish={handleSearch}>
            <Form.Item name="intent_type" label="类型" initialValue="">
              <Select style={{ width: isMobile ? '100%' : 180 }} options={intentTypeOptions} />
            </Form.Item>
            <Form.Item name="keyword" label="关键字">
              <Input placeholder="债权编号/名称/申请人/电话" allowClear style={{ width: isMobile ? '100%' : 260 }} />
            </Form.Item>
            <Form.Item name="submit_date" label="提交日">
              <DatePicker allowClear style={{ width: isMobile ? '100%' : undefined }} disabledDate={disabledSubmitDate} />
            </Form.Item>
            <Form.Item>
              <Space wrap>
                <Button type="primary" htmlType="submit">查询</Button>
                <Button onClick={handleReset}>重置</Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>

        <Card className="card-compact">
          <Table
            rowKey={(r) => String(r?.id)}
            loading={loading}
            dataSource={records}
            columns={columns}
            size={isMobile ? 'small' : 'middle'}
            pagination={false}
            scroll={{ x: 'max-content' }}
            onChange={handleTableChange}
            onRow={(record) => ({
              onClick: () => openDetail(record),
              style: { cursor: 'pointer' },
            })}
          />
          <div className="c-pagination-bar">
            <Pagination
              current={pagination.current}
              pageSize={pagination.pageSize}
              total={pagination.total}
              simple={isMobile}
              showSizeChanger={isMobile ? false : PAGINATION_ENABLE_SIZE_CHANGER}
              pageSizeOptions={PAGINATION_PAGE_SIZE_OPTIONS}
              onChange={handlePaginationChange}
            />
          </div>
        </Card>
      </div>

      <Drawer
        title="意向详情"
        placement={isMobile ? 'bottom' : 'right'}
        width={isMobile ? undefined : 520}
        height={isMobile ? '75vh' : undefined}
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
      >
        <Space direction="vertical" style={{ width: '100%' }} size={16}>
          <Descriptions size="small" bordered column={isMobile ? 1 : 2}>
            <Descriptions.Item label="类型">{intentTypeLabel(detail?.intentType)}</Descriptions.Item>
            <Descriptions.Item label="提交时间">{detail?.createTime ? dayjs(detail.createTime).format('YYYY-MM-DD HH:mm') : '-'}</Descriptions.Item>
            <Descriptions.Item label="申请人">{detail?.applicantName || '-'}</Descriptions.Item>
            <Descriptions.Item label="联系电话">{detail?.contactPhone || '-'}</Descriptions.Item>
            <Descriptions.Item label="债权编号">{detail?.debtNo || '-'}</Descriptions.Item>
            <Descriptions.Item label="债权名称">{detail?.debtTitle || '-'}</Descriptions.Item>
            <Descriptions.Item label="债权类型">{labelOfDebtType(detail?.debtType)}</Descriptions.Item>
            <Descriptions.Item label="合计金额">{detail?.totalAmount != null ? formatAmountText(detail.totalAmount) : '-'}</Descriptions.Item>
            <Descriptions.Item label="债权状态">{labelOfDebtStatus(detail?.debtStatus)}</Descriptions.Item>
            <Descriptions.Item label="备注" span={isMobile ? 1 : 2}>{detail?.message || '-'}</Descriptions.Item>
          </Descriptions>

          <Space wrap>
            <Button type="primary" onClick={() => openDebtDetail(detail)} disabled={!detail?.debtId}>查看债权详情</Button>
            <Button onClick={() => setDetailOpen(false)}>关闭</Button>
          </Space>

          <Typography.Text type="secondary">
            提示：当前仅展示提交到您名下债权的投资/代理催收意向记录。
          </Typography.Text>
        </Space>
      </Drawer>
    </div>
  );
};

export default MyDebtIntentsPage;
